import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EASE_LUXE } from "@/components/Reveal";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function GoogleButton({ label }) {
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-button"
      className="flex w-full items-center justify-center gap-3 rounded-full border border-white/20 py-4 text-xs uppercase tracking-luxe text-white transition-colors hover:bg-white hover:text-black">
      <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.7 34.6 27 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.6 5.6C41.4 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
      {label}
    </button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier, password);
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 pt-24" data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE_LUXE }} className="w-full max-w-md">
        <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Welcome back</p>
        <h1 className="font-heading text-5xl font-semibold leading-none tracking-tight text-white">Sign in</h1>

        <div className="mt-10"><GoogleButton label="Continue with Google" /></div>
        <div className="my-8 flex items-center gap-4 text-[10px] uppercase tracking-luxe text-white/30">
          <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-8">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-luxe text-white/40">Username or email</label>
            <input required value={identifier} data-testid="login-identifier"
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full border-b border-white/15 bg-transparent py-3 text-sm text-white focus:border-velvet-gold focus:outline-none" />
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-luxe text-white/40">Password</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-b border-white/15 bg-transparent py-3 pr-10 text-sm text-white focus:border-velvet-gold focus:outline-none" />
              {password && (
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  data-testid="login-show-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-white/50 hover:text-velvet-gold">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-velvet-rose" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full bg-white py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-rose hover:text-white disabled:opacity-50">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-8 text-sm font-light text-white/50">
          No account? <Link to="/register" className="text-velvet-gold">Create one</Link>
        </p>
      </motion.div>
    </div>
  );
}
